import { useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { DialogContainer, DialogTitle, DialogActions } from '../../common/components';

// Tabs componentes
import GeneralConfigTab from './tabs/GeneralConfigTab';
import GroupsTab from './tabs/GroupsTab';
import AdditionalsTab from './tabs/AdditionalsTab';
import ScoreCoefficientsTab from './tabs/ScoreCoefficientsTab';
import PreAdjReasonsTab from './tabs/PreAdjReasonsTab';
import ConstructionTypesTab from './tabs/ConstructionTypesTab';
import PeiValuesTab from './tabs/PeiValuesTab';
import FeeTypesTab from './tabs/FeeTypesTab';
import FeeStructuresTab from './tabs/FeeStructuresTab';
import PaymentMethodsTab from './tabs/PaymentMethodsTab';
import PaymentMethodDetailsTab from './tabs/PaymentMethodDetailsTab';

// Hook personalizado para el diálogo
import { usePlanEditDialog } from '../hooks/usePlanEditDialog';
import { DIALOG_TABS } from '../constants/plansConstants';

/**
 * Diálogo de edición de plan con múltiples tabs
 * Refactorizado siguiendo los patrones del proyecto
 */
const PlanEditDialog = ({
  open,
  onClose,
  editMode,
  selectedPlan,
  branches,
  feeTypes,
  onInputChange,
  onSelectChange,
  onSave,
}) => {
  const [activeTab, setActiveTab] = useState(DIALOG_TABS.GENERAL_CONFIG);

  const {
    // Estados para cada tab
    groupsTabProps,
    additionalsTabProps,
    scoreCoefficientsTabProps,
    preAdjReasonsTabProps,
    constructionTypesTabProps,
    peiValuesTabProps,
    feeTypesTabProps,
    feeStructuresTabProps,
    paymentMethodsTabProps,
    paymentMethodDetailsTabProps,

    // Funciones de manejo
    handleTabChange,
  } = usePlanEditDialog({
    selectedPlan,
    activeTab,
    setActiveTab,
  });

  const planName = editMode ? selectedPlan.name : '';
  return (
    <DialogContainer open={open} onClose={onClose} size='fullWidth'>
      <DialogTitle>{editMode ? `Editar plan: ${planName}` : 'Agregar plan'}</DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          scrollButtons="auto"
          variant="scrollable"
        >
          <Tab label="Config. del Plan" />
          <Tab label="Grupos" />
          <Tab label="Adicion." />
          <Tab label="Coef. de Puntaje" />
          <Tab label="Motivos Preadj." />
          <Tab label="Tip. Constr." />
          <Tab label="Valores PEI" />
          <Tab label="Tip. Cuotas" />
          <Tab label="Estr. Cuotas" />
          <Tab label="F. de Pagos" />
          <Tab label="Det. F. de Pagos" />
        </Tabs>
      </Box>

      {/* Tab de Configuración General */}
      {activeTab === DIALOG_TABS.GENERAL_CONFIG && (
        <GeneralConfigTab
          selectedPlan={selectedPlan}
          branches={branches}
          onInputChange={onInputChange}
          onSelectChange={onSelectChange}
        />
      )}

      {/* Tab de Grupos */}
      {activeTab === DIALOG_TABS.GROUPS && <GroupsTab {...groupsTabProps} />}

      {/* Tab de Adicionales */}
      {activeTab === DIALOG_TABS.ADDITIONALS && editMode && (
        <AdditionalsTab {...additionalsTabProps} />
      )}

      {/* Tab de Coeficientes de Puntaje */}
      {activeTab === DIALOG_TABS.SCORE_COEFFICIENTS && editMode && (
        <ScoreCoefficientsTab {...scoreCoefficientsTabProps} />
      )}

      {/* Tab de Motivos de Preadjudicación */}
      {activeTab === DIALOG_TABS.PRE_ADJ_REASONS && editMode && (
        <PreAdjReasonsTab {...preAdjReasonsTabProps} />
      )}

      {/* Tab de Tipos de Construcción */}
      {activeTab === DIALOG_TABS.CONSTRUCTION_TYPES && editMode && (
        <ConstructionTypesTab {...constructionTypesTabProps} />
      )}

      {/* Tab de Valores PEI */}
      {activeTab === DIALOG_TABS.PEI_VALUES && editMode && <PeiValuesTab {...peiValuesTabProps} />}

      {/* Tab de Tipos de Cuotas */}
      {activeTab === DIALOG_TABS.FEE_TYPES && editMode && <FeeTypesTab {...feeTypesTabProps} />}

      {/* Tab de Estructura de Cuotas */}
      {activeTab === DIALOG_TABS.FEE_STRUCTURES && editMode && (
        <FeeStructuresTab {...feeStructuresTabProps} />
      )}

      {/* Tab de Formas de Pago */}
      {activeTab === DIALOG_TABS.PAYMENT_METHODS && editMode && (
        <PaymentMethodsTab {...paymentMethodsTabProps} />
      )}

      {/* Tab de Detalle de Formas de Pago */}
      {activeTab === DIALOG_TABS.PAYMENT_METHOD_DETAILS && editMode && (
        <PaymentMethodDetailsTab {...paymentMethodDetailsTabProps} />
      )}

      <DialogActions handleClose={onClose} handleSave={onSave} />
    </DialogContainer>
  );
};

export default PlanEditDialog;
